import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import type { BrowserMode } from "@/types/convex";

/**
 * Hook to manage level browser mode (none, categories, whitelist).
 * 
 * Key principle: Mode changes ONLY update the browserMode field - they never modify
 * blueJayRules categories, allowlist, or blocklist. This allows settings to persist
 * when switching between modes.
 * 
 * Note: browserMode is stored separately from blueJayRules.
 * 
 * Sidebar visibility by mode:
 * - none: Settings only
 * - whitelist: Settings, Whitelist only
 * - categories: All sections (Settings, Categories, Whitelist, Blacklist)
 */
export function useLevelBrowserMode(levelId: string) {
  const { toast } = useToast();
  
  // Get level data
  const levelsData = useQuery(api.levels.list.all, {
    paginationOpts: { cursor: null, numItems: 100 }
  });
  
  // Get all website categories for whitelist mode detection (legacy fallback)
  const { categories: allCategories } = useWebsiteCategories();
  
  // Mutation for updating level policies - sends browserMode directly
  const updateLevelPolicies = useMutation(api.levels.policies.update);
  
  // Find the current level
  const currentLevel = levelsData?.page?.find(level => level._id === levelId);
  
  // Get blueJayRules from level
  const blueJayRules = currentLevel?.blueJayRules;
  
  /**
   * Determine current browser mode.
   * Priority: 
   * 1. Explicit browserMode from level config (where backend stores it)
   * 2. Inferred from blueJayRules structure (fallback for backwards compatibility)
   */
  const getBrowserMode = (): BrowserMode => {
    // Read explicit browserMode from level if available
    const explicitMode = (currentLevel as any)?.browserMode;
    if (explicitMode && ["none", "categories", "whitelist"].includes(explicitMode)) {
      return explicitMode as BrowserMode;
    }
    
    // Fallback: infer from blueJayRules for backwards compatibility
    
    // blueJayRules = false means browser disabled
    if ((blueJayRules as unknown) === false) {
      return "none";
    }
    
    // Empty object or no blueJayRules means browser disabled
    if (!blueJayRules || (typeof blueJayRules === 'object' && Object.keys(blueJayRules as object).length === 0)) {
      return "none";
    }
    
    // Get categories from blueJayRules
    const rulesCategories = typeof blueJayRules === 'object' ? (blueJayRules as any)?.categories : [];
    
    // Whitelist mode = ONLY when ALL categories are blocked
    const totalCategoryCount = allCategories.length;
    const blockedCategoryCount = rulesCategories?.length ?? 0;
    
    if (totalCategoryCount > 0 && blockedCategoryCount === totalCategoryCount) {
      return "whitelist";
    }
    
    // Any other case is filtered/categories mode
    return "categories";
  };
  
  const browserMode = getBrowserMode();
  
  /**
   * Set the browser mode.
   * 
   * Sends browserMode directly to backend - does NOT modify blueJayRules categories,
   * allowlist, or blocklist. This allows settings to persist when switching between modes.
   */
  const setBrowserMode = async (mode: BrowserMode) => {
    if (!levelId || !currentLevel) {
      toast({
        title: "Error",
        description: "Level ID not available",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Send browserMode directly to backend
      // Cast to any since api.ts types may not include browserMode yet
      await updateLevelPolicies({
        level: levelId,
        browserMode: mode,
      } as any);
      
      toast({
        title: "Browser Mode Updated",
        description: `Level browser mode set to ${mode}.`,
      });
    } catch (error) {
      console.error("Error updating browser mode:", error);
      toast({
        title: "Error",
        description: "Failed to update browser mode",
        variant: "destructive",
      });
    }
  };
  
  return {
    browserMode,
    setBrowserMode,
    isLoading: levelsData === undefined,
  };
}
